<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<script lang="ts">
  import Panel from '@components/panel.svelte'
  import {
    ordinalNumber,
    tokensToText,
    type GameraTeamReference,
    type NflDrive,
    type NflPlayEvent,
    type NflDriveEndReason,
  } from '@statmuse/core/gamera'
  import { events } from './stores'
  import Icon from '@components/icon.svelte'
  import { hexToRgba } from '@lib/color'
  import { last } from 'lodash-es'
  import NflProgressField from './nfl-progress-field.svelte'

  export let drive: NflDrive
  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined
  export let live = false

  const endOfDrive = (reason?: NflDriveEndReason) => {
    switch (reason) {
      case 'punt':
      case 'touchdown':
      case 'fumble':
      case 'safety':
      case 'downs':
      case 'interception':
        return `${reason[0].toUpperCase()}${reason.slice(1)}`
      case 'fieldGoal':
        return 'Field Goal'
      case 'missedFieldGoal':
        return 'Missed FG'
      case 'muffedFieldGoal':
        return 'Muffed FG'
      case 'blockedFieldGoal':
      case 'blockedFieldGoalDowns':
        return 'Blocked FG'
      case 'blockedPunt':
      case 'blockedPuntDowns':
        return 'Blocked Punt'
      case 'blockedPuntSafety':
      case 'blockedFieldGoalSafety':
      case 'fumbleSafety':
        return 'Safety'
      case 'endOfGame':
        return 'End of Game'
      case 'endOfHalf':
        return 'End of Half'
      case 'unknown':
        return ''
      default:
        return reason
    }
  }

  let isOpen = live && drive.endReason === 'unknown'

  let plays: NflPlayEvent[]

  $: {
    plays = $events
      .filter((e) => e.driveSequence === drive.driveSequence)
      .filter((e) => e.type === 'play')

    if (live) {
      plays = [...plays].reverse()
    }
  }

  $: firstPlay = live ? last(plays) : plays[0]
  $: lastPlay = live ? plays[0] : last(plays)

  $: scrimmagePlays = plays.filter((p) =>
    ['pass', 'rush', 'play', 'penalty'].includes(p.playType),
  )
  $: firstScrimmagePlay = live ? last(scrimmagePlays) : scrimmagePlays[0]
  $: lastScrimmagePlay = live ? scrimmagePlays[0] : last(scrimmagePlays)

  $: possessionTeamId =
    firstPlay?.playType === 'kickoff' || firstPlay?.playType === 'freeKick'
      ? firstPlay?.endSituation.possessionTeamId
      : firstPlay?.startSituation.possessionTeamId

  $: teamMap = {
    [awayTeam?.teamId ?? 0]: awayTeam,
    [homeTeam?.teamId ?? 0]: homeTeam,
  }

  $: team = teamMap[possessionTeamId ?? 0]
</script>

<Panel
  background={team?.colors?.backgroundColor}
  foreground={team?.colors?.foregroundColor}
>
  <div
    class="flex justify-between cursor-pointer"
    on:click={() => (isOpen = !isOpen)}
  >
    <div class="flex items-center gap-3">
      <Icon name={!isOpen ? 'dropdown' : 'dropdown-up'} class="w-4 h-4" />

      <div
        class="rounded-2xl px-2"
        style={isOpen && team?.colors
          ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
          : undefined}
      >
        {live && drive.endReason === 'unknown'
          ? 'Current'
          : endOfDrive(drive.endReason)}
      </div>

      <span class="opacity-50">
        {drive.playCount} play{drive.playCount > 1 ? 's' : ''},
        {drive.gainYards} yd,
        {drive.duration}
      </span>
    </div>
    {#if plays.length > 0}
      <div>{lastPlay?.awayPoints}-{lastPlay?.homePoints}</div>
    {/if}
  </div>
  {#if isOpen}
    <div class="mt-2">
      <NflProgressField
        firstPlayLocation={firstScrimmagePlay?.startSituation.location}
        lastPlayLocation={lastScrimmagePlay?.startSituation.possessionTeamId !==
          lastScrimmagePlay?.endSituation.possessionTeamId &&
        lastScrimmagePlay?.startSituation.down === 4
          ? lastScrimmagePlay?.endSituation.location
          : lastScrimmagePlay?.startSituation.possessionTeamId !==
            lastScrimmagePlay?.endSituation.possessionTeamId
          ? lastScrimmagePlay?.startSituation.location
          : new RegExp('touchdown', 'i').test(
              tokensToText(lastScrimmagePlay?.description),
            ) && lastScrimmagePlay?.flags.isScoringPlay
          ? {
              yardLine: 0,
              teamId:
                lastScrimmagePlay?.startSituation.possessionTeamId ===
                awayTeam?.teamId
                  ? homeTeam?.teamId
                  : awayTeam?.teamId,
            }
          : lastScrimmagePlay?.endSituation.location}
        fillColor={team?.colors?.foregroundColor}
        possessionTeam={possessionTeamId === awayTeam?.teamId ? 'away' : 'home'}
        opponentSide={firstPlay?.playType === 'kickoff' ||
        firstPlay?.playType === 'freeKick'
          ? firstPlay.endSituation.location.teamId !== possessionTeamId
          : firstPlay?.startSituation.location.teamId !== possessionTeamId}
      />
    </div>
    <div class="flex flex-col divide-y">
      {#each plays as play (play)}
        <div
          class="-mx-3 px-3 py-2"
          style={team?.colors
            ? `border-color:${hexToRgba(team.colors.foregroundColor, 0.5)}`
            : undefined}
        >
          <div class="flex gap-2 opacity-50">
            <span>
              {play.period <= 4 ? `${play.period}Q` : 'OT'}
              {play.startSituation.clock}
            </span>
            {#if play.startSituation.down}
              <span>
                {ordinalNumber(play.startSituation.down)} & {possessionTeamId !==
                  play.startSituation.location.teamId &&
                (play.startSituation.yardsToFirst ?? 0) -
                  play.startSituation.location.yardLine ===
                  0
                  ? 'Goal'
                  : play.startSituation.yardsToFirst} at {teamMap[
                  play.startSituation.location.teamId
                ]?.abbreviation}
                {play.startSituation.location.yardLine}
              </span>
            {/if}
          </div>
          {tokensToText(play.description)}
        </div>
      {/each}
    </div>
  {/if}
</Panel>
